import * as React from "react";
import { Clock } from "lucide-react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

export interface TimePickerFieldProps {
  id?: string;
  /** HH:mm, 24-hour. */
  value: string;
  onChange: (value: string) => void;
  label: React.ReactNode;
  error?: string;
  disabled?: boolean;
  className?: string;
}

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = ["00", "15", "30", "45"];

function splitValue(value: string): { hour: string; minute: string } {
  const [h, m] = value.split(":");
  const hour = HOURS.includes(h) ? h : "09";
  // Snap to the nearest offered minute step rather than silently dropping
  // an odd value (e.g. a pre-filled reschedule time like 09:07).
  const minute = MINUTES.includes(m)
    ? m
    : MINUTES.reduce((closest, candidate) =>
        Math.abs(Number(candidate) - Number(m || 0)) <
        Math.abs(Number(closest) - Number(m || 0))
          ? candidate
          : closest,
      );
  return { hour, minute };
}

/**
 * Brand-styled time picker (no native <input type="time"> — that renders
 * the browser's own unstyled widget, off-brand and inconsistent across
 * Chrome/Safari/Firefox). Two Select dropdowns (hour, minute in 15-minute
 * steps) laid out side by side, matching the rest of the design system.
 */
export function TimePickerField({
  id,
  value,
  onChange,
  label,
  error,
  disabled,
  className,
}: TimePickerFieldProps): React.ReactElement {
  const { hour, minute } = splitValue(value);

  return (
    <div className={cn("space-y-2", className)}>
      {label && (
        <label
          htmlFor={id}
          className="text-sm font-medium leading-none"
        >
          {label}
        </label>
      )}
      <div className="flex items-center gap-2">
        <Clock className="size-4 shrink-0 text-muted-foreground" />
        <Select
          value={hour}
          onValueChange={(h) => onChange(`${h}:${minute}`)}
          disabled={disabled}
        >
          <SelectTrigger id={id} className={cn("h-10 w-20", error && "border-destructive")}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-64">
            {HOURS.map((h) => (
              <SelectItem key={h} value={h}>
                {h}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-muted-foreground">:</span>
        <Select
          value={minute}
          onValueChange={(m) => onChange(`${hour}:${m}`)}
          disabled={disabled}
        >
          <SelectTrigger className={cn("h-10 w-20", error && "border-destructive")}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MINUTES.map((m) => (
              <SelectItem key={m} value={m}>
                {m}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}
